import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Field, Input, Select, Button, AddableSelect } from "@/lib/ui";

const EMPTY = {
  company_name: "", trade_name: "", gst: "", pan: "", address: "", city: "", locality: "",
  contact_person: "", whatsapp: "", email: "", stock_location: "", years_in_business: 0,
};

export default function ProfileForm({ initial, onSaved, submitLabel = "Save Profile" }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [filters, setFilters] = useState({ cities: [], localities: [], stock_locations: [] });
  const [loading, setLoading] = useState(false);

  const loadFilters = () => api.get("/public/filters").then(({ data }) => setFilters(data)).catch(() => {});
  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { if (initial) setForm((f) => ({ ...f, ...initial })); }, [initial]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v, ...(k === "city" ? { locality: "" } : {}) }));

  const addMaster = async (kind, name, city) => {
    await api.post(`/seller/master/${kind}`, { name, city });
    await loadFilters();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.city || !form.locality || !form.contact_person || !form.whatsapp) {
      toast.error("Please fill all required fields"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.put("/seller/profile", { ...form, years_in_business: Number(form.years_in_business) || 0 });
      toast.success("Company profile saved");
      onSaved && onSaved(data);
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  const locs = filters.localities.filter((l) => !form.city || l.city === form.city);

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      <Field label="Company Name" required><Input data-testid="pf-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
      <Field label="Trade Name"><Input data-testid="pf-trade" value={form.trade_name} onChange={(e) => set("trade_name", e.target.value)} /></Field>
      <Field label="GST Number"><Input data-testid="pf-gst" value={form.gst} onChange={(e) => set("gst", e.target.value.toUpperCase())} /></Field>
      <Field label="PAN"><Input data-testid="pf-pan" value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} /></Field>
      <Field label="Company Address"><Input data-testid="pf-address" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
      <Field label="Stock Location" hint="Pick or add your own">
        <AddableSelect testid="pf-stockloc" addTestid="pf-stockloc-add" value={form.stock_location} placeholder="Select stock location"
          options={filters.stock_locations.map((s) => s.name)} onChange={(v) => set("stock_location", v)}
          onAdd={(name) => addMaster("stock_locations", name)} />
      </Field>
      <Field label="City" required>
        <AddableSelect testid="pf-city" addTestid="pf-city-add" value={form.city} placeholder="Select City"
          options={filters.cities.map((c) => c.name)} onChange={(v) => set("city", v)}
          onAdd={(name) => addMaster("cities", name)} />
      </Field>
      <Field label="Locality" required>
        <AddableSelect testid="pf-locality" addTestid="pf-locality-add" value={form.locality} placeholder="Select Locality"
          disabled={!form.city} options={locs.map((l) => l.name)} onChange={(v) => set("locality", v)}
          onAdd={(name) => addMaster("localities", name, form.city)} />
      </Field>
      <Field label="Contact Person" required><Input data-testid="pf-contact" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field>
      <Field label="WhatsApp Number" required><Input data-testid="pf-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))} /></Field>
      <Field label="Email"><Input data-testid="pf-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
      <Field label="Years in Business"><Input data-testid="pf-years" type="number" value={form.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} /></Field>
      <div className="sm:col-span-2">
        <Button data-testid="pf-submit" type="submit" disabled={loading} className="w-full sm:w-auto">{loading ? "Saving…" : submitLabel}</Button>
      </div>
    </form>
  );
}
